import { useEffect, useState, type HTMLAttributes } from 'react'
import { ExchangeChartOrderControls } from './ExchangeChartOrderControls'

/** Shared drag targets and keyboard controls for every visible chart pane. */
export function useExchangeChartReordering(
  exchanges: string[],
  moveExchange: (exchange: string, targetExchange: string) => void,
  context: string,
  labelSuffix: string | ((id: string) => string) = '',
) {
  const [draggedExchange, setDraggedExchange] = useState<string | null>(null)
  const [dropTarget, setDropTarget] = useState<string | null>(null)
  const clearDrag = () => { setDraggedExchange(null); setDropTarget(null) }

  useEffect(() => {
    setDraggedExchange(null)
    setDropTarget(null)
  }, [context])

  const getDropProps = (exchange: string): HTMLAttributes<HTMLDivElement> => ({
    className: dropTarget === exchange && draggedExchange !== exchange ? 'rounded-lg ring-2 ring-teal-400' : '',
    onDragOver: (event) => {
      if (!draggedExchange || !exchanges.includes(draggedExchange)) return
      event.preventDefault()
      event.dataTransfer.dropEffect = 'move'
      setDropTarget(exchange)
    },
    onDragLeave: (event) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDropTarget(null)
    },
    onDrop: (event) => {
      if (!draggedExchange || !exchanges.includes(draggedExchange)) return
      event.preventDefault()
      moveExchange(draggedExchange, exchange)
      clearDrag()
    },
  })

  const renderControls = (exchange: string) => {
    const index = exchanges.indexOf(exchange)
    return (
      <ExchangeChartOrderControls
        exchange={typeof labelSuffix === 'function' ? labelSuffix(exchange) : `${exchange}${labelSuffix}`}
        canMoveUp={index > 0}
        canMoveDown={index >= 0 && index < exchanges.length - 1}
        onMoveUp={() => moveExchange(exchange, exchanges[index - 1])}
        onMoveDown={() => moveExchange(exchange, exchanges[index + 1])}
        onDragStart={(event) => {
          event.dataTransfer.effectAllowed = 'move'
          event.dataTransfer.setData('text/plain', exchange)
          setDraggedExchange(exchange)
        }}
        onDragEnd={clearDrag}
      />
    )
  }

  return { getDropProps, renderControls }
}
